import { useEffect, useState } from 'react';
import {
  CONTROL_PANEL_PATHS,
  operatorPlanListResponseSchema,
  type OperatorAuditEntry,
} from '@forecastify/shared';
import { useOperatorSession } from '../session';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ An audit row's *Változás* column: `Team → Business`, `reason: …`.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Values, never labels.** The before/after diff carries ids, slugs and free text in whatever
 * shape the action wrote; this prints the fields that changed, side by side, and translates nothing
 * — a reason is the operator's own words and an id is an id. The mockup's column is the same: data.
 *
 * ⚠️ **A key present on only one side is printed alone**, so a creation reads as its values and a
 * removal as what was removed, rather than as `undefined → …`.
 *
 * ⚠️ 3 Nov 2026 · **a key the after side names as EMPTY is a clearing, and says so** — `4 → törölve`.
 * Printed alone it read `4`, exactly like the row that set the limit to 4. `removed` is the one
 * word this translates, passed in because the function has no catalogue of its own.
 *
 * ⚠️ 3 Nov 2026 · **one exception to *values, never labels*: a plan id reads as the plan's name**
 * (product owner). A uuid names nothing an operator can recognise, the plan list is a few rows the
 * console already reads, and an id with no plan behind it still prints as itself.
 */
export function describeChange(
  entry: OperatorAuditEntry,
  removed: string,
  planNames: ReadonlyMap<string, string> = new Map(),
): string {
  const before = asRecord(entry.before);
  const after = asRecord(entry.after);
  const keys = [...new Set([...Object.keys(after), ...Object.keys(before)])];

  return keys
    .map((key) => {
      const name = (value: unknown): string => {
        const shown = show(value);
        return key === 'plan_id' ? (planNames.get(shown) ?? shown) : shown;
      };
      const was = name(before[key]);
      const now = name(after[key]);
      if (was === '' && now === '') return '';
      if (was === '') return now;
      if (now === '') return key in after ? `${was} → ${removed}` : was;
      if (was === now) return now;
      return `${was} → ${now}`;
    })
    .filter((part) => part !== '')
    .join(' · ');
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function show(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

/** The plans by id, for `describeChange`. Empty until (and unless) the list arrives. */
export function usePlanNames(): ReadonlyMap<string, string> {
  const session = useOperatorSession();
  const [names, setNames] = useState<ReadonlyMap<string, string>>(new Map());
  useEffect(() => {
    void session.request<unknown>(CONTROL_PANEL_PATHS.plans).then((result) => {
      if (!result.ok) return;
      const parsed = operatorPlanListResponseSchema.safeParse(result.value);
      if (parsed.success) setNames(new Map(parsed.data.data.map((plan) => [plan.id, plan.name])));
    });
  }, [session]);
  return names;
}
